/// <reference types="vite/client" />
/**
 * API Client for MarketXtractor Backend
 * Replaces direct OpenAI/Gemini API calls from frontend
 */

const API_BASE_URL = import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_BASE_URL || 'https://automation.marketxtractor.ai/api');

export interface ToolExecutionRequest {
    tool_id: string;
    user_input: string;
    provider?: 'openai' | 'gemini';
}

export interface ToolExecutionResponse {
    success: boolean;
    data: any | null;
    error: string | null;
    execution_time_ms: number;
    provider: string;
}

/**
 * Execute an AI tool via backend API.
 * This replaces the direct AI API calls (OpenAI/Gemini).
 */
export const executeToolAPI = async (
    toolId: string,
    userInput: string,
    provider: 'openai' | 'gemini' = 'openai'
): Promise<ToolExecutionResponse> => {
    console.log(`🔗 [API CLIENT] Calling backend: POST /api/tools/execute`);
    console.log(`🛠️ [API CLIENT] Tool: ${toolId}`);
    console.log(`🤖 [API CLIENT] Provider: ${provider}`);

    const response = await fetch(`${API_BASE_URL}/tools/execute`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-API-Key': import.meta.env.VITE_BACKEND_API_KEY || 'dev-key-12345'
        },
        body: JSON.stringify({
            tool_id: toolId,
            user_input: userInput,
            provider: provider
        } as ToolExecutionRequest)
    });

    if (!response.ok) {
        const error = await response.text();
        console.error(`❌ [API CLIENT] Request failed: ${response.status}`);
        throw new Error(`Backend API error: ${error}`);
    }

    const result: ToolExecutionResponse = await response.json();

    if (result.success) {
        console.log(`✅ [API CLIENT] Tool executed in ${result.execution_time_ms}ms`);
    } else {
        console.error(`❌ [API CLIENT] Tool execution failed: ${result.error}`);
    }

    return result;
};

/**
 * Health check endpoint
 */
export const healthCheck = async (): Promise<{ status: string; version: string }> => {
    const response = await fetch(`${API_BASE_URL}/health`);
    return await response.json();
};
